import React, { useState, useEffect, useRef } from 'react';
import './../../App.css';
import './SearchPage.css';
import Header from './../../components/Header';
import Footer from '../../components/Footer';
import ApiService from '../../services/ApiService';
import AnimeCardComponent from '../../components/animecard/AnimeCardComponent';
import RatingRangeSlider from './RatingRageSlider';

const GENRES = [
    "Авангард", "Гурман", "Дзёсей", "Драма", "Комедия", "Музыка", "Повседневность", 
    "Приключения", "Романтика", "Сверхъестественное", "Сёдзё", "Сёнен", 
    "Спорт", "Сэйнэн", "Тайна", "Триллер", "Ужасы", "Фантастика",
    "Фэнтези", "Экшен", "Эротика", "Этти"
];

// Create a closure over the original function
const originalSearchFilteredAnime = ApiService.searchFilteredAnime;

// Keep track of what was already requested
const requestCache = new Map();

// Monkey patch the API service
ApiService.searchFilteredAnime = function(params) {
    // Add timestamp to identify the call in logs
    const timestamp = new Date().toISOString();
    
    // Check if this exact request is already in our cache
    if (requestCache.has(params)) {
        console.log(`[${timestamp}] CACHE HIT for request: ${params}`);
        // Return the cached promise to ensure consistent behavior
        return requestCache.get(params);
    }
    
    // Log the request
    console.log(`[${timestamp}] Making API request: ${params}`);
    
    // Make the call and cache the promise
    const promise = originalSearchFilteredAnime(params);
    requestCache.set(params, promise);
    
    return promise;
};

const SearchPage = () => {
    const [scrollPosition, setScrollPosition] = useState(0);
    const [animeList, setAnimeList] = useState([]);
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState(0);
    const [thatsAll, setThatsAll] = useState(false);

    // Form state
    const [query, setQuery] = useState('');
    const [selectedGenres, setSelectedGenres] = useState([]);
    const [studio, setStudio] = useState('');
    const [movie, setMovie] = useState(false);
    const [hq, setHq] = useState(false);
    const [ratingRange, setRatingRange] = useState({ min: 0, max: 10 });
    const [viral, setViral] = useState(false);

    // Separate state for applied filters
    const [appliedFilters, setAppliedFilters] = useState({
        query: '',
        selectedGenres: [],
        studio: '',
        movie: false,
        hq: false,
        minRating: '',
        maxRating: '',
        viral: false
    });

    const scrollTimeoutRef = useRef(null);
    const searchTimeoutRef = useRef(null);
    const filterTimeoutRef = useRef(null);
    const fetchingRef = useRef(false); // Track if we're currently fetching
    const pagesRequestedRef = useRef(new Set()); // Track which pages we've requested

    // Create a debug function to log filter changes
    const logFilters = (message, filters) => {
        console.log(`[DEBUG ${message}]`, JSON.stringify(filters));
    };

    const buildQueryParams = (pageNum) => {
        const params = new URLSearchParams();
        params.append("page", pageNum);
        
        // Log current applied filters for debugging
        logFilters("Building query with filters", appliedFilters);
        
        if (appliedFilters.query) params.append("query", appliedFilters.query);
        if (appliedFilters.selectedGenres.length > 0) params.append("genres", appliedFilters.selectedGenres.join(','));
        if (appliedFilters.studio) params.append("studio", appliedFilters.studio);
        if (appliedFilters.movie) params.append("movie", true);
        if (appliedFilters.hq) params.append("hq", true);
        if (appliedFilters.minRating) params.append("minRating", appliedFilters.minRating);
        if (appliedFilters.maxRating) params.append("maxRating", appliedFilters.maxRating);
        if (appliedFilters.viral) params.append("viral", true);
        
        const queryString = params.toString();
        console.log(`[DEBUG Query] ${queryString}`);
        return queryString;
    };

    const fetchData = async (pageNum) => {
        // Create a unique key for this filter+page combination
        const queryParams = buildQueryParams(pageNum);
        const requestKey = queryParams;
        
        console.log(`[DEBUG fetchData] Attempting to fetch page ${pageNum} with params: ${queryParams}`);
        console.log(`[DEBUG fetchData] Current filters:`, appliedFilters);
        
        // Check if this specific request is already in progress or has been made
        if (fetchingRef.current) {
            console.log(`[DEBUG fetchData] Already fetching, skipping`);
            return;
        }
        
        if (pageNum > 0 && pagesRequestedRef.current.has(requestKey)) {
            console.log(`[DEBUG fetchData] Page already requested, skipping`);
            return;
        }
        
        if (thatsAll && pageNum > 0) {
            console.log(`[DEBUG fetchData] That's all flag is set, skipping`);
            return;
        }

        // Mark that we're fetching and this page has been requested
        fetchingRef.current = true;
        pagesRequestedRef.current.add(requestKey);
        setLoading(true);
        
        try {
            console.log(`[DEBUG fetchData] Making API call for page ${pageNum}`);
            const data = await ApiService.searchFilteredAnime(queryParams);

            if (data.type === "success") {
                const animeArray = data.data.anime || [];
                console.log(`[DEBUG fetchData] Received ${animeArray.length} results`);
                
                if (animeArray.length === 0) {
                    setThatsAll(true);
                    console.log(`[DEBUG fetchData] No results, setting thatsAll=true`);
                } else {
                    // Use a function form of setState to ensure we're working with the latest state
                    setAnimeList(prev => {
                        // If this is page 0, we want to replace the existing list
                        if (pageNum === 0) {
                            console.log(`[DEBUG fetchData] Setting page 0 results, replacing list`);
                            return animeArray;
                        }
                        // Otherwise, append to the existing list
                        console.log(`[DEBUG fetchData] Appending results to existing list`);
                        return [...prev, ...animeArray];
                    });
                }
            } else {
                console.error('Error fetching anime list:', data);
            }
        } catch (error) {
            console.error("Search error", error);
        } finally {
            setLoading(false);
            fetchingRef.current = false;
            console.log(`[DEBUG fetchData] Finished fetching, reset loading and fetchingRef`);
        }
    };

    const handleScroll = () => {
        if (scrollTimeoutRef.current) return;

        scrollTimeoutRef.current = setTimeout(() => {
            const position = window.scrollY + window.innerHeight;
            const bottom = document.documentElement.offsetHeight;
            const buffer = 500;

            if (position >= bottom - buffer && !loading && !thatsAll && !fetchingRef.current) {
                setPage(prevPage => prevPage + 1);
            }

            setScrollPosition(window.scrollY);
            scrollTimeoutRef.current = null;
        }, 200);
    };

    const resetAndSearch = () => {
        setAnimeList([]);
        setPage(0);
        setThatsAll(false);
        pagesRequestedRef.current = new Set(); // Clear the tracking set
        fetchData(0); // Immediately fetch data after reset
    };

    const handleGenreChange = (genre) => {
        const newGenres = selectedGenres.includes(genre) 
            ? selectedGenres.filter(g => g !== genre) 
            : [...selectedGenres, genre];
        
        setSelectedGenres(newGenres);
        
        // Apply filter with delay
        if (filterTimeoutRef.current) {
            clearTimeout(filterTimeoutRef.current);
        }
        
        filterTimeoutRef.current = setTimeout(() => {
            setAppliedFilters(prev => ({
                ...prev,
                selectedGenres: newGenres
            }));
            resetAndSearch();
        }, 500);
    };

    // Handle text field changes with debounce
    const handleTextFieldChange = (field, value) => {
        if (field === 'query') {
            setQuery(value);
        } else if (field === 'studio') {
            setStudio(value);
        }
        
        if (searchTimeoutRef.current) {
            clearTimeout(searchTimeoutRef.current);
        }
        
        searchTimeoutRef.current = setTimeout(() => {
            setAppliedFilters(prev => ({
                ...prev,
                [field]: value
            }));
            resetAndSearch();
        }, 1000);
    };

    // Handle rating range changes
    const handleRatingChange = (range) => {
        setRatingRange(range);
        
        if (searchTimeoutRef.current) {
            clearTimeout(searchTimeoutRef.current);
        }
        
        searchTimeoutRef.current = setTimeout(() => {
            setAppliedFilters(prev => ({
                ...prev,
                minRating: range.min > 0 ? range.min : '',
                maxRating: range.max < 10 ? range.max : ''
            }));
            resetAndSearch();
        }, 500);
    };

    // Handle checkbox changes immediately
    const handleCheckboxChange = (field, checked) => {
        // Update local state
        if (field === 'movie') {
            setMovie(checked);
        } else if (field === 'hq') {
            setHq(checked);
        } else if (field === 'viral') {
            setViral(checked);
        }
        
        // Directly update applied filters and trigger search
        setAppliedFilters(prev => ({
            ...prev,
            [field]: checked
        }));
        
        // Clear previous results immediately
        setAnimeList([]);
        setPage(0);
        setThatsAll(false);
        pagesRequestedRef.current = new Set();
        
        // Force a fetch in the next tick to ensure state updates are applied
        setTimeout(() => {
            fetchData(0);
        }, 10);
    };

    useEffect(() => {
        window.addEventListener('scroll', handleScroll, { passive: true });
        return () => {
            window.removeEventListener('scroll', handleScroll);
            // Clean up any pending timeouts
            if (searchTimeoutRef.current) {
                clearTimeout(searchTimeoutRef.current);
            }
            if (filterTimeoutRef.current) {
                clearTimeout(filterTimeoutRef.current);
            }
        };
    }, [loading, thatsAll]);

    // Effect to fetch when page changes or when applied filters change
    useEffect(() => {
        if (page > 0) { // Only fetch for page > 0, page 0 is handled separately
            fetchData(page);
        }
    }, [page]);
    
    // Effect to fetch when filters change
    useEffect(() => {
        // Reset and fetch when filters change
        if (Object.keys(appliedFilters).length > 0) {
            setAnimeList([]);
            setPage(0);
            setThatsAll(false);
            pagesRequestedRef.current = new Set();
            fetchData(0);
        }
    }, [appliedFilters]);
    
    // Initial fetch on component mount
    useEffect(() => {
        fetchData(0);
    }, []);

    // Split genres into two columns
    const halfLength = Math.ceil(GENRES.length / 2);
    const leftColumnGenres = GENRES.slice(0, halfLength);
    const rightColumnGenres = GENRES.slice(halfLength);

    return (
        <div className="app">
            <Header scrolled={scrollPosition > 0} />
            <div className="content article search-page">
                      <h1 className='tasty-title'>Расширенный поиск</h1>
                        <div className='tasty-underline'></div>
                <div className="search-bar-top">
                    <input
                        type="text"
                        placeholder="Название тайтла"
                        value={query}
                        onChange={e => handleTextFieldChange('query', e.target.value)}
                    />
                </div>

                <div className="container-wrapper search-container">
                    <div className="filters-sidebar">
                        <div className="filter-group">
                            <h3>Фильтры</h3>
                            <div className="genres-columns">
                                <div className="genres-column">
                                    {leftColumnGenres.map((genre) => (
                                        <label key={genre}>
                                            <input
                                                type="checkbox"
                                                checked={selectedGenres.includes(genre)}
                                                onChange={() => handleGenreChange(genre)}
                                            />
                                            <span>{genre}</span>
                                        </label>
                                    ))}
                                </div>
                                <div className="genres-column">
                                    {rightColumnGenres.map((genre) => (
                                        <label key={genre}>
                                            <input
                                                type="checkbox"
                                                checked={selectedGenres.includes(genre)}
                                                onChange={() => handleGenreChange(genre)}
                                            />
                                            <span>{genre}</span>
                                        </label>
                                    ))}
                                </div>
                            </div>
                        </div>

                        <div className="filter-group">
                            {/* <label>
                                Студия:
                                <input
                                    type="text"
                                    value={studio}
                                    onChange={e => handleTextFieldChange('studio', e.target.value)}
                                />
                            </label> */}
                            <label>
                                <input
                                    type="checkbox"
                                    checked={movie}
                                    onChange={e => handleCheckboxChange('movie', e.target.checked)}
                                />
                                <span>Только полнометражные</span>
                            </label>
                            <label>
                                <input
                                    type="checkbox"
                                    checked={hq}
                                    onChange={e => handleCheckboxChange('hq', e.target.checked)}
                                />
                                <span>Высокое качество (QHD, 4K)</span>
                            </label>
                            
                            <div className="rating-filter">
                                <label>Рейтинг:</label>
                                <RatingRangeSlider 
                                    minValue={ratingRange.min}
                                    maxValue={ratingRange.max}
                                    onChange={handleRatingChange}
                                />
                            </div>
                            
                            <label>
                                <input
                                    type="checkbox"
                                    checked={viral}
                                    onChange={e => handleCheckboxChange('viral', e.target.checked)}
                                />
                                <span>Вирусное</span>
                            </label>
                        </div>
                    </div>

                    <div className="anime-results">
                  
                        <div className="anime-container">
                            {animeList.map((anime, index) => (
                                <AnimeCardComponent key={`${anime.id || index}`} anime={anime} isInteractive={true} />
                            ))}
                        </div>
                        {loading && <p>Загружаем...</p>}
                        {!loading && !thatsAll && animeList.length > 0 && (
                            <p className="scroll-more-label">Прокрутите ниже, чтобы загрузить больше</p>
                        )}
                        {thatsAll && animeList.length > 0 && <p>Больше ничего не найдено.</p>}
                        {thatsAll && animeList.length === 0 && <p>Ничего не найдено по вашему запросу.</p>}
                    </div>
                </div>
            </div>
            <Footer />
        </div>
    );
};

export default SearchPage;